import React, { useEffect, useState } from 'react';
import styles from './MandateThirdPartyTemplate.module.css';
import { getClient, getClientHasThirdPartySubsets } from '../../../Redux/Store/Slices/client';
import { useAppSelector } from '../../../Redux/Store/Store';
import { axiosInstance } from '../../../Authentication/AxiosInterceptor';
import { MandateTemplateRequest } from '../../Portal/TemplateEditor/MandateTemplate/MandateTemplate';
import NoItems from '../../../Components/Utils/NoItems/NoItems';
import { toast } from 'react-toastify';
import OrganisationCard from '../../../Components/Utils/OrganisationCard/OrganisationCard';
import { ISelectionItem } from '../../../Types/Analyse/ISelectionItem';

type Category<T> = {
    id: string;
    title: string;
    data: ISelectionItem<T>[];
};

const MandateThirdPartyTemplate = () => {

    const client = useAppSelector(getClient);
    const hasThirdPartySubsets = useAppSelector(getClientHasThirdPartySubsets);

    // Fetching data related to the client
    const [categories, setCategories] = React.useState<Category<string>[]>([]);
    const [noTemplates, setNoTemplates] = React.useState<boolean>(false);

    useEffect(() => {
        const fetchData = async () => {
            if (!client) return;
            const response = await axiosInstance.get(`GetMandateClientSubsetsInfo/${client.id}`);
            setCategories(response.data);
            setNoTemplates(response.data.flatMap((category: Category<string>) => category.data).length === 0);
        };
        fetchData();
    }, [client]);

    const mandateTemplateHandler = async (subsetId: string, templateId: string, add: boolean) => {
        try {
            if (!client) return;

            const request: MandateTemplateRequest = {
                templateId: templateId,
                clientId: client.id,
                subsetId: subsetId,
                add: add,
            };

            await axiosInstance.post(
                `MandateTemplateToSubset`,
                request
            );

            const message = add ? 'Template mandated successfully' : 'Template mandate removed successfully';
            toast.success(message);
        } catch (err) {
            console.error("Failed to mandate template:", err);
            toast.error('Failed to update template mandate');
        }
    };

    const handleChange = (updatedCategory: Category<string>) => {
        setCategories(current => {
            // find the old version of this category
            const oldCat = current.find(c => c.id === updatedCategory.id);
            if (oldCat) {
                // for each item, if `selected` changed, call the endpoint
                updatedCategory.data.forEach(item => {
                    const oldItem = oldCat.data.find(i => i.id === item.id);
                    if (oldItem && oldItem.selected !== item.selected) {
                        mandateTemplateHandler(oldCat.id, item.value, item.selected);
                    }
                });
            }

            // then replace the category in state
            return current.map(cat =>
                cat.id === updatedCategory.id ? updatedCategory : cat
            );
        });
    };

    if (!hasThirdPartySubsets) {
        return (
            <div className={styles.container}>
                <h2>No Third-Party Organizations or Departments</h2>
                <p>This client does not have any third-party organizations or departments set up.</p>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <p>Select to allocate templates to organisations and departments. Use the checkboxes to mandate or remove template access.</p>

            <div className={styles.contentWrapper}>
                {/* Organisations Section */}
                <div className={styles.section}>
                    <h3>
                        Organisations
                        <span className={styles.countBadge}>
                            {categories.filter(cat => cat.title.includes('Partner')).length}
                        </span>
                    </h3>
                    {categories.filter(cat => cat.title.includes('Partner')).length === 0 ? (
                        <NoItems textAlign="top" message="No organisations have been set up." />
                    ) : (
                        <div className={styles.organisationCards}>
                            {categories
                                .filter(cat => cat.title.includes('Partner'))
                                .map(category => (
                                    <OrganisationCard
                                        key={category.id}
                                        organisationName={category.title}
                                        templates={category.data}
                                        onChange={(updatedTemplates) => {
                                            const updatedCategory = { ...category, data: updatedTemplates };
                                            handleChange(updatedCategory);
                                        }}
                                        selectAllDisabled={false}
                                    />
                                ))}
                        </div>
                    )}
                </div>

                {/* Departments Section */}
                <div className={styles.section}>
                    <h3>
                        Departments
                        <span className={styles.countBadge}>
                            {categories.filter(cat => cat.title.includes('Department')).length}
                        </span>
                    </h3>
                    {categories.filter(cat => cat.title.includes('Department')).length === 0 ? (
                        <NoItems textAlign="top" message="No departments have been set up." />
                    ) : (
                        <div className={styles.organisationCards}>
                            {categories
                                .filter(cat => cat.title.includes('Department'))
                                .map(category => (
                                    <OrganisationCard
                                        key={category.id}
                                        organisationName={category.title}
                                        templates={category.data}
                                        onChange={(updatedTemplates) => {
                                            const updatedCategory = { ...category, data: updatedTemplates };
                                            handleChange(updatedCategory);
                                        }}
                                        selectAllDisabled={false}
                                    />
                                ))}
                        </div>
                    )}
                </div>

                <div className={styles.adminNote}>
                    <h3>For all other admin functions, please return to the <a href="https://app.mesma.io/AdminControls" target="_blank" rel="noopener noreferrer">original admin control panel</a>.</h3>
                </div>
            </div>
        </div>
    );
};

export default MandateThirdPartyTemplate;